<template>
  <div
    :class="{
      [$style.item]: true,
      [$style.activeItem]: isActive
    }"
  >
    <div :class="$style.content">
      <base-userpic
        v-if="hasPhoto"
        :src="photoSrc"
        :class="$style.icon"
      />
      <base-icon
        v-else-if="hasIcon"
        :type="iconType"
        :class="$style.icon"
      />
      <div :class="$style.itemTitles">
        <span :class="[$style.itemTitle, $style.itemPrimaryTitle]"> {{ primaryTitle }} </span>
        <span
          v-if="secondaryTitle"
          :class="[$style.itemTitle, $style.itemSecondaryTitle]"
        >
          {{ secondaryTitle }}
        </span>
      </div>

      <hide-button
        v-if="canHide"
        @click.stop="handleHideClick"
      />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { PhotoHelper } from '@/modules/applicant-photo-editor/helper';
import BaseUserpic from '@/shared/legacy/ui/userpic/userpic.vue';
import { EntityType } from '@/shared/legacy/api/photo';
import { useGlobal } from '@/shared/legacy/lib/use-global';
import BaseIcon from '@/shared/legacy/ui/base-icon/icon.vue';
import { hideContact } from '../api';
import HideButton from './hide-button.vue';
import { isAuthResource, isMember as isMemberContact, isUnknown, useHiddenContactsCache } from './model';

export default {
  name: 'EmailAutocompleteItem',
  components: {
    HideButton,
    BaseUserpic,
    BaseIcon
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    },
    isSearch: {
      type: Boolean,
      default: false
    },
    isSuggest: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { $trlMessage } = useGlobal();
    const store = useStore();
    const { addHiddenContact, removeHiddenContact } = useHiddenContactsCache();

    const accountRef = computed(() => store.getters['config/account']);
    /**
     * @duplicate in frontend/src/components/email-autocomplete-list/email-list-item.vue
     * @type {ComputedRef<unknown>}
     */
    const isMe = computed(
      () =>
        props.item.member_type === accountRef.value.id
        || props.item.member === accountRef.value.id /* нужно оставить, потому что в проп selectedItems извне всё еще могут прилетать такие данные */
        || props.item.user_id === accountRef.value.id
        || props.item.id?.startsWith?.(accountRef.value.id)
    );

    const primaryTitle = computed(() =>
      isMe.value ? $trlMessage('Me') : props.item.name || props.item.email
    );
    const secondaryTitle = computed(() =>
      primaryTitle.value !== props.item.email ? props.item.email : null
    );

    const photoSrc = computed(() =>
      PhotoHelper.getPhotoUrl({
        entity: props.item,
        type: EntityType.USER
      })
    );

    /**
     * Т.к. компонент рисует разные сущности из разных запросов (suggest, поиск и возможно еще каких-то)
     * @refactor Разделить компонент на несколько для отрисовки каждого типа контакта. Убрать лишние условия (isSearch, isSuggest)
     */
    const isMember = computed(
      () => props.item.member_type === 'member'
        || ((props.isSearch || props.isSuggest) && isMemberContact(props.item))
        || Boolean(props.item.account_member_id)
    );

    const iconType = computed(() => {
      if (Boolean(props.item.member_type) || isMember.value) {
        return 'circle-profile';
      }
      if (props.item.resource) {
        return 'circle-chair';
      }
      return undefined;
    });

    const hasPhoto = computed(() => isMember.value && Boolean(photoSrc.value));
    const hasIcon = computed(() => Boolean(iconType.value));

    const handleHideClick = () => {
      addHiddenContact(props.item);
      hideContact(props.item).catch(() => {
        removeHiddenContact(props.item);
      });
    };

    const canHide = computed(() => {
      if (!props.isSearch) {
        return false;
      }

      return !isMember.value && !isAuthResource(props.item) && !isUnknown(props.item);
    });

    return {
      photoSrc,
      iconType,
      hasPhoto,
      hasIcon,
      primaryTitle,
      secondaryTitle,
      canHide,
      handleHideClick
    };
  }
};
</script>

<style module>
.item {
  user-select: none;
  letter-spacing: 0;
  padding: 0 8px;
  width: 100%;
}

.icon {
  width: 22px;
  height: 22px;
  margin-right: 7px;
  flex: none;
}

.content {
  display: flex;
  align-items: flex-start;
  font-size: 15px;
  line-height: 24px;
  padding: 8px;
  border-radius: 8px;
}

.activeItem .content {
  background-color: $black-7;
}

.itemTitles {
  display: grid;
  @mixin ellipsis;
}

.itemTitle {
  font-size: 14px;
  line-height: 20px;
  @mixin ellipsis;
}

.itemPrimaryTitle {
  color: $textColor;
}

.itemSecondaryTitle {
  color: $black-60; /* #929097 */
  font-size: 12px;
  line-height: 16px;
}
</style>

<i18n lang="json">{}</i18n>
